/* ==========================================================================
   Custom Luxury Product Page — ES6
   ========================================================================== */
(() => {
  'use strict';

  class CustomProductPage {
    constructor(section) {
      this.section = section;
      const jsonEl = section.querySelector('[data-cpp-product-json]');
      this.data = jsonEl ? JSON.parse(jsonEl.textContent) : null;
      
      this.form = section.querySelector('form[action*="/cart/add"]');
      this.currentVariant = this.data?.variants[0] || null;

      this.initGallery();
      this.initVariants();
      this.initQuantity();
      this.initDelivery();
      this.initAccordion();
      this.initModal();
      this.initShare();
      this.initWishlist();
    }

    initGallery() {
      this.gallery = this.section.querySelector('[data-cpp-gallery]');
      if (!this.gallery) return;

      this.thumbs = [...this.gallery.querySelectorAll('[data-cpp-thumb]')];
      this.slides = [...this.gallery.querySelectorAll('[data-cpp-slide]')];
      this.dots = [...this.gallery.querySelectorAll('.cpp-gallery__dot')];
      this.counter = this.gallery.querySelector('[data-cpp-counter]');
      this.track = this.gallery.querySelector('[data-cpp-track]');
      this.thumbTrack = this.gallery.querySelector('[data-cpp-thumbs]');
      
      // Desktop vertical arrows
      const upBtn = this.gallery.querySelector('[data-cpp-thumbs-up]');
      const downBtn = this.gallery.querySelector('[data-cpp-thumbs-down]');
      
      if(upBtn && downBtn && this.thumbTrack) {
        upBtn.addEventListener('click', () => { this.thumbTrack.scrollBy({ top: -122, behavior: 'smooth' }); });
        downBtn.addEventListener('click', () => { this.thumbTrack.scrollBy({ top: 122, behavior: 'smooth' }); });
      }

      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.goToSlide(index));
      });

      // Mobile swipe sync
      if (this.track && window.innerWidth <= 989) {
        this.track.addEventListener('scroll', () => {
          const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.updateActiveIndicators(index);
        });

        // Functional Mobile Navigation Arrows
        const mobPrev = this.gallery.querySelector('[data-cpp-mob-prev]');
        const mobNext = this.gallery.querySelector('[data-cpp-mob-next]');
        
        if(mobPrev) {
          mobPrev.addEventListener('click', () => {
            this.track.scrollBy({ left: -this.track.clientWidth, behavior: 'smooth' });
          });
        }
        if(mobNext) {
          mobNext.addEventListener('click', () => {
            this.track.scrollBy({ left: this.track.clientWidth, behavior: 'smooth' });
          });
        }
      }
    }

    goToSlide(index) {
      if (!this.slides[index]) return;
      if (window.innerWidth > 989) {
        this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      } else {
        this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
      }
      this.updateActiveIndicators(index);
    }

    updateActiveIndicators(index) {
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
    }

    initVariants() {
      this.inputs = [...this.section.querySelectorAll('[data-cpp-option-input]')];
      this.inputs.forEach(input => {
        input.addEventListener('change', () => this.onVariantChange());
      });
    }

    onVariantChange() {
      const selectedOptions = this.data.options.map((opt, idx) => {
        const input = this.inputs.find(i => i.name === `options[${opt}]` && i.checked);
        return input ? input.value : null;
      });

      const matchedVariant = this.data.variants.find(v => 
        v.options.every((val, i) => val === selectedOptions[i])
      );

      if (matchedVariant) {
        this.currentVariant = matchedVariant;
        this.updateDOM();
      }
    }

    updateDOM() {
      const v = this.currentVariant;
      
      const idInput = this.section.querySelector('[data-cpp-variant-id]');
      if(idInput) idInput.value = v.id;

      // Price update
      const priceEl = this.section.querySelector('[data-cpp-price]');
      const compareEl = this.section.querySelector('[data-cpp-compare]');
      const badgeEl = this.section.querySelector('[data-cpp-discount]');
      
      if(priceEl) priceEl.textContent = v.priceFormatted;
      if(compareEl) {
        compareEl.hidden = !v.compareAtPriceFormatted;
        compareEl.textContent = v.compareAtPriceFormatted || '';
      }
      if(badgeEl) {
        if(v.compareAtPrice > v.price) {
          badgeEl.hidden = false;
          const discount = Math.round(((v.compareAtPrice - v.price) / v.compareAtPrice) * 100);
          badgeEl.textContent = `${discount}% OFF`;
        } else {
          badgeEl.hidden = true;
        }
      }

      // Dynamic Inventory Update
      const stockEl = this.section.querySelector('[data-cpp-stock]');
      if(stockEl) {
        const threshold = parseInt(stockEl.dataset.threshold, 10) || 5;
        if (v.inventoryManagement === 'shopify') {
          if (v.inventoryQuantity > threshold) stockEl.innerHTML = `<span class="cpp-stock__status cpp-stock__status--in">🟢 In Stock</span>`;
          else if (v.inventoryQuantity > 0) stockEl.innerHTML = `<span class="cpp-stock__status cpp-stock__status--low">🟠 Only ${v.inventoryQuantity} Left</span>`;
          else stockEl.innerHTML = `<span class="cpp-stock__status cpp-stock__status--out">🔴 Sold Out</span>`;
        } else {
          stockEl.innerHTML = `<span class="cpp-stock__status ${v.available ? 'cpp-stock__status--in' : 'cpp-stock__status--out'}">${v.available ? '🟢 In Stock' : '🔴 Sold Out'}</span>`;
        }
      }

      // Buttons Update
      const atcBtn = this.section.querySelector('[data-cpp-atc]');
      const atcText = this.section.querySelector('[data-cpp-atc-text]');
      if(atcBtn) {
        atcBtn.disabled = !v.available;
        atcText.textContent = v.available ? 'Add to Cart' : 'Sold Out';
      }

      window.history.replaceState({}, '', `${window.location.pathname}?variant=${v.id}`);

      const enquiryVar = this.section.querySelector('[data-cpp-enquiry-variant]');
      if(enquiryVar) enquiryVar.value = v.title;

      if(v.featuredMediaId) {
        const targetSlide = this.slides.findIndex(s => Number(s.dataset.mediaId) === v.featuredMediaId);
        if(targetSlide !== -1) this.goToSlide(targetSlide);
      }
    }

    initQuantity() {
      const minus = this.section.querySelector('[data-cpp-qty-minus]');
      const plus = this.section.querySelector('[data-cpp-qty-plus]');
      const input = this.section.querySelector('.cpp-qty-box__input');
      
      if(minus && plus && input) {
        minus.addEventListener('click', () => { input.value = Math.max(1, parseInt(input.value || 1) - 1); });
        plus.addEventListener('click', () => { input.value = parseInt(input.value || 1) + 1; });
      }
    }

    initDelivery() {
      const el = this.section.querySelector('[data-cpp-delivery-date]');
      if(!el) return;
      
      const format = el.dataset.format || 'long';
      const today = new Date();
      const d1 = new Date(today); d1.setDate(d1.getDate() + 5);
      const d2 = new Date(today); d2.setDate(d2.getDate() + 7);
      
      const options = format === 'short' 
        ? { day: '2-digit', month: '2-digit' } 
        : { day: 'numeric', month: 'long' };
        
      el.textContent = `${d1.toLocaleDateString('en-US', options)} – ${d2.toLocaleDateString('en-US', options)}`;
    }

    initAccordion() {
      const items = this.section.querySelectorAll('.cpp-accordion__item');
      items.forEach(item => {
        const btn = item.querySelector('.cpp-accordion__btn');
        const content = item.querySelector('.cpp-accordion__content');
        
        if(item.classList.contains('is-open')) content.style.maxHeight = content.scrollHeight + 'px';

        btn.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');
          items.forEach(i => {
            i.classList.remove('is-open');
            i.querySelector('.cpp-accordion__content').style.maxHeight = null;
          });
          if (!isOpen) {
            item.classList.add('is-open');
            content.style.maxHeight = content.scrollHeight + 'px';
          }
        });
      });
    }

    initModal() {
      const trigger = this.section.querySelector('[data-cpp-enquiry-trigger]');
      const modal = this.section.querySelector('[data-cpp-enquiry-modal]');
      const closers = this.section.querySelectorAll('[data-cpp-modal-close]');
      
      if(trigger && modal) {
        const openModal = () => { modal.hidden = false; document.body.style.overflow = 'hidden'; };
        const closeModal = () => { modal.hidden = true; document.body.style.overflow = ''; };
        trigger.addEventListener('click', openModal);
        closers.forEach(c => c.addEventListener('click', closeModal));
        document.addEventListener('keydown', e => { if(e.key === 'Escape' && !modal.hidden) closeModal(); });
      }
    }

    initWishlist() {
      const btn = this.section.querySelector('[data-cpp-wishlist]');
      if (!btn) return;
      
      const productId = btn.dataset.productId;
      const wishlistKey = 'cpp_wishlist_items';
      let currentWishlist = JSON.parse(localStorage.getItem(wishlistKey)) || [];

      // Check load state
      if (currentWishlist.includes(productId)) {
        btn.classList.add('is-active');
        btn.querySelector('svg').setAttribute('fill', 'currentColor');
      }

      // Toggle functionality
      btn.addEventListener('click', () => {
        currentWishlist = JSON.parse(localStorage.getItem(wishlistKey)) || [];
        
        if (currentWishlist.includes(productId)) {
          // Remove from wishlist
          currentWishlist = currentWishlist.filter(id => id !== productId);
          btn.classList.remove('is-active');
          btn.querySelector('svg').setAttribute('fill', 'none');
        } else {
          // Add to wishlist
          currentWishlist.push(productId);
          btn.classList.add('is-active');
          btn.querySelector('svg').setAttribute('fill', 'currentColor');
        }
        
        localStorage.setItem(wishlistKey, JSON.stringify(currentWishlist));
      });
    }

    initShare() {
      const btns = this.section.querySelectorAll('[data-cpp-share]');
      btns.forEach(btn => {
        btn.addEventListener('click', async () => {
          const type = btn.dataset.cppShare;
          const url = window.location.href;
          if(type === 'link') {
            await navigator.clipboard.writeText(url);
            alert('Link copied to clipboard!');
          } else if(type === 'whatsapp') { window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(url)}`, '_blank');
          } else if(type === 'facebook') { window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
          }
        });
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.cpp[data-section-id]').forEach(section => new CustomProductPage(section));
  });
})();